/**
 * «Mes a mes»: compras, pagos y saldo al cierre.
 *
 * ES EL BLOQUE QUE CONTESTA "CÓMO FLUCTUÓ LA DEUDA", y el que reemplaza al
 * gráfico que se descartó. La pregunta real no es la forma de la curva: es
 * «¿la deuda con este tipo viene creciendo?», y tres números por mes la
 * contestan igual de rápido, sin meterle una librería de gráficos al proyecto
 * por un solo sparkline.
 *
 * UNA TABLA POR MONEDA. No se suman nunca; si el proveedor debe en las dos, hay
 * dos tablas, cada una con su encabezado. El cierre de cada mes arrastra el
 * acumulado de los anteriores: sin eso la columna miente.
 */
import type { Moneda } from "@tora/shared";

import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";

import { resumenPorMes, type MovimientoProveedor } from "./logica";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2026-09" → "sep", o "sep 2026" cuando el resumen cruza de año. */
function etiquetaMes(mes: string, conAnio: boolean): string {
  const [anio, m] = mes.split("-");
  const nombre = MESES[Number(m) - 1] ?? mes;
  return conAnio ? `${nombre} ${anio}` : nombre;
}

export interface ResumenPorMesProps {
  movimientos: ReadonlyArray<MovimientoProveedor>;
  /** En qué monedas tuvo movimientos este proveedor. Una tabla por cada una. */
  monedas: ReadonlyArray<Moneda>;
}

export function ResumenPorMes({ movimientos, monedas }: ResumenPorMesProps): JSX.Element | null {
  const tablas = monedas
    .map((moneda) => ({ moneda, filas: resumenPorMes(movimientos, moneda) }))
    .filter((t) => t.filas.length > 0);

  if (tablas.length === 0) return null;

  return (
    <div className="space-y-2">
      <TituloSeccion>Mes a mes</TituloSeccion>
      <div className="grid gap-3 lg:grid-cols-2">
        {tablas.map(({ moneda, filas }) => {
          const anios = new Set(filas.map((f) => f.mes.slice(0, 4)));
          const conAnio = anios.size > 1;
          return (
            <Card key={moneda} padding="none" className="overflow-hidden">
              <Table densidad="compacta">
                <TableHeader>
                  <TableRow>
                    <TableHead>{moneda === "USD" ? "En dólares" : "En pesos"}</TableHead>
                    <TableHead className="text-right">Compras</TableHead>
                    <TableHead className="text-right">Pagos</TableHead>
                    <TableHead className="text-right">Al cierre</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas.map((f) => (
                    <TableRow key={f.mes}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {etiquetaMes(f.mes, conAnio)}
                      </TableCell>
                      {/* Compras y pagos sin chip: el encabezado de la tabla ya
                          declaró la moneda. El cierre sí lo lleva, porque es el
                          número que alguien va a leer suelto. */}
                      <TableCellImporte
                        monto={f.compras}
                        moneda={moneda}
                        tamano="sm"
                        codigo={false}
                      />
                      <TableCellImporte
                        monto={f.pagos}
                        moneda={moneda}
                        tamano="sm"
                        codigo={false}
                      />
                      <TableCellImporte monto={f.cierre} moneda={moneda} tamano="sm" />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
